import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Song } from '../../types';

type LoopMode = 'none' | 'one' | 'all';

interface MusicPlayerProps {
  currentSong: Song | null;
  isPlaying: boolean;
  isPaused: boolean;
  isLoading: boolean;
  currentTime: number;
  volume: number;
  loopMode: LoopMode;
  shuffleEnabled: boolean;
  queue: Song[];
  onPlayPause: () => void;
  onStop: () => void;
  onPrevious: () => void;
  onNext: () => void;
  onSeek: (time: number) => void;
  onVolumeChange: (volume: number) => void;
  onLoopModeChange: (mode: LoopMode) => void;
  onShuffleToggle: () => void;
  onClose: () => void;
  onToggleQueue: () => void;
}

export const MusicPlayer: React.FC<MusicPlayerProps> = ({
  currentSong,
  isPlaying,
  isPaused,
  isLoading,
  currentTime,
  volume,
  loopMode,
  shuffleEnabled,
  queue,
  onPlayPause,
  onStop,
  onPrevious,
  onNext,
  onSeek,
  onVolumeChange,
  onLoopModeChange,
  onShuffleToggle,
  onClose,
  onToggleQueue
}) => {
  const [isSeeking, setIsSeeking] = useState(false);
  const [seekPosition, setSeekPosition] = useState(0); // 0-1 percentage
  const [hoverPosition, setHoverPosition] = useState<number | null>(null); // 0-1 percentage for preview
  const [isDraggingVolume, setIsDraggingVolume] = useState(false);
  const [lastValidSong, setLastValidSong] = useState<Song | null>(currentSong);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const volumeBarRef = useRef<HTMLDivElement>(null);
  const seekingSongRef = useRef<Song | null>(null);
  const lastSeekUpdateRef = useRef<number>(0);
  const hasDraggedRef = useRef<boolean>(false);
  const seekDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Keep track of the last valid song to prevent disappearing during drag
  useEffect(() => {
    if (currentSong) {
      setLastValidSong(currentSong);
    }
  }, [currentSong]);

  // Use last valid song if current is null (during state updates)
  const displaySong = currentSong || lastValidSong;

  // Calculate position percentage from mouse event
  const getPositionFromEvent = useCallback((e: MouseEvent | React.MouseEvent): number => {
    if (!progressBarRef.current) return 0;
    const rect = progressBarRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    return Math.max(0, Math.min(1, x / rect.width));
  }, []);

  // Handle seeking drag
  const handleSeekMove = useCallback((e: MouseEvent) => {
    if (!isSeeking || !seekingSongRef.current) return;
    const position = getPositionFromEvent(e);
    setSeekPosition(position);
    hasDraggedRef.current = true;
    
    // Hybrid approach: debounce frequency updates when paused
    // Only update if user pauses at a position for 200ms
    if (isPaused) {
      // Clear previous debounce timer
      if (seekDebounceRef.current) {
        clearTimeout(seekDebounceRef.current);
      }
      
      // Set new debounce timer
      seekDebounceRef.current = setTimeout(() => {
        const seekTime = position * seekingSongRef.current!.duration;
        onSeek(seekTime);
      }, 200);
    }
  }, [isSeeking, isPaused, getPositionFromEvent, onSeek]);

  const handleSeekEnd = useCallback(() => {
    if (!isSeeking || !seekingSongRef.current) return;
    
    // Clear any pending debounced seek
    if (seekDebounceRef.current) {
      clearTimeout(seekDebounceRef.current);
      seekDebounceRef.current = null;
    }
    
    const seekTime = seekPosition * seekingSongRef.current.duration;
    
    // Always call onSeek to handle clicks and set final position
    // For paused state: if it was a click (no drag), this is the only seek call
    // For paused state: if it was a drag, this sets the final position after debounced updates
    // For playing state: this seeks and resumes playback
    onSeek(seekTime);
    
    setIsSeeking(false);
    setHoverPosition(null);
    seekingSongRef.current = null;
    hasDraggedRef.current = false;
  }, [isSeeking, seekPosition, onSeek]);

  // Handle hover preview
  const handleProgressMouseEnter = (e: React.MouseEvent) => {
    if (!isSeeking) setHoverPosition(0);
    e.stopPropagation();
  };

  const handleProgressMouseMove = (e: React.MouseEvent) => {
    if (isSeeking) return;
    e.stopPropagation();
    const position = getPositionFromEvent(e);
    setHoverPosition(position);
  };

  const handleProgressMouseLeave = (e: React.MouseEvent) => {
    if (!isSeeking) setHoverPosition(null);
    e.stopPropagation();
  };

  const handleVolumeMouseMove = (e: MouseEvent) => {
    if (!isDraggingVolume || !volumeBarRef.current) return;
    const rect = volumeBarRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
    onVolumeChange(percentage);
  };

  const handleVolumeMouseUp = () => {
    setIsDraggingVolume(false);
  };

  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS
  useEffect(() => {
    if (isSeeking) {
      document.addEventListener('mousemove', handleSeekMove);
      document.addEventListener('mouseup', handleSeekEnd);
      return () => {
        document.removeEventListener('mousemove', handleSeekMove);
        document.removeEventListener('mouseup', handleSeekEnd);
      };
    }
  }, [isSeeking, handleSeekMove, handleSeekEnd]);

  useEffect(() => {
    if (isDraggingVolume) {
      document.addEventListener('mousemove', handleVolumeMouseMove);
      document.addEventListener('mouseup', handleVolumeMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleVolumeMouseMove);
        document.removeEventListener('mouseup', handleVolumeMouseUp);
      };
    }
  }, [isDraggingVolume, onVolumeChange]);

  // Explicitly calculate whether to show animation
  const showAnimation = isPlaying && !isPaused && !isLoading;

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(0)} KB`;
    }
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const handleProgressMouseDown = (e: React.MouseEvent) => {
    if (!displaySong) return;
    e.preventDefault();
    e.stopPropagation();
    seekingSongRef.current = displaySong;
    const position = getPositionFromEvent(e);
    setSeekPosition(position);
    setIsSeeking(true);
    setHoverPosition(null);
    hasDraggedRef.current = false;
  };

  const handleVolumeBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!volumeBarRef.current) return;
    const rect = volumeBarRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
    onVolumeChange(percentage);
  };

  const handleVolumeMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDraggingVolume(true);
    handleVolumeBarClick(e);
  };

  const safeQueue = queue || [];
  const currentQueueIndex = displaySong ? safeQueue.findIndex(s => s.id === displaySong.id) : -1;
  const hasNext = currentQueueIndex < safeQueue.length - 1 || loopMode === 'all' || shuffleEnabled;
  const hasPrevious = currentQueueIndex > 0 || loopMode === 'all' || shuffleEnabled;

  // Calculate current progress (clamped to 0-1 range)
  const songDuration = displaySong?.duration || 0;
  const currentProgress = songDuration > 0 
    ? Math.min(1, Math.max(0, currentTime / songDuration)) 
    : 0;
  const displayProgress = isSeeking ? seekPosition : currentProgress;
  const displayTime = Math.min(displayProgress * songDuration, songDuration);
  
  // Calculate hover preview
  const previewProgress = hoverPosition !== null ? hoverPosition : displayProgress;
  const previewTime = previewProgress * songDuration;

  const cycleLoopMode = () => {
    // Cycle: Current (one) → All → Off (none) → Current...
    const modes: LoopMode[] = ['one', 'all', 'none'];
    const currentIndex = modes.indexOf(loopMode);
    const nextMode = modes[(currentIndex + 1) % modes.length];
    onLoopModeChange(nextMode);
  };

  return (
    <div className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-2xl border-t border-purple-400">
      <div className="max-w-7xl mx-auto px-4 py-3">
        {/* Progress Bar */}
        <div className="mb-2 pt-1">
          <div 
            ref={progressBarRef}
            className="relative h-1.5 bg-white/20 rounded-full cursor-pointer"
            onMouseDown={handleProgressMouseDown}
            onMouseEnter={handleProgressMouseEnter}
            onMouseMove={handleProgressMouseMove}
            onMouseLeave={handleProgressMouseLeave}
          >
            {/* Filled progress */}
            <div 
              className="absolute h-full bg-white rounded-full pointer-events-none"
              style={{ 
                width: `${displayProgress * 100}%`,
                transition: (isSeeking || isPaused) ? 'none' : 'width 0.1s linear'
              }}
            />
            
            {/* Hover preview (shows where you'll seek to) */}
            {hoverPosition !== null && (
              <div 
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white/60 rounded-full shadow-lg pointer-events-none"
                style={{ left: `calc(${previewProgress * 100}% - 6px)` }}
              />
            )}
            
            {/* Current position handle */}
            <div 
              className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg pointer-events-none transition-opacity ${
                hoverPosition !== null || isSeeking ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ left: `calc(${displayProgress * 100}% - 6px)` }}
            />
          </div>
          
          {/* Time labels */}
          <div className="flex justify-between text-[10px] text-purple-100 mt-0.5">
            <span>{formatDuration(displayTime)}</span>
            <span>{formatDuration(songDuration)}</span>
          </div>
        </div>

        <div className="flex items-center justify-between gap-4">
          {/* Song Info */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Animated Music Icon or Loading Spinner */}
            <div className="flex items-center gap-1 flex-shrink-0">
              {isLoading ? (
                <div className="animate-spin h-6 w-6 border-2 border-white border-t-transparent rounded-full"></div>
              ) : showAnimation ? (
                <>
                  <div className="w-1 h-4 bg-white rounded-full animate-pulse" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-1 h-6 bg-white rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-1 h-5 bg-white rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></div>
                  <div className="w-1 h-7 bg-white rounded-full animate-pulse" style={{ animationDelay: '450ms' }}></div>
                </>
              ) : (
                <svg className="h-6 w-6 text-white/80" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                </svg>
              )}
            </div>

            {/* Song Details */}
            <div className="flex-1 min-w-0">
              {isLoading ? (
                <>
                  <div className="text-sm font-semibold text-white/60">
                    Loading song...
                  </div>
                  <div className="text-xs text-purple-100">
                    Please wait
                  </div>
                </>
              ) : displaySong ? (
                <>
                  <div className="text-sm font-semibold truncate">
                    {displaySong.name}
                  </div>
                  <div className="text-xs text-purple-100 truncate">
                    {displaySong.artist || 'Unknown Artist'} • Track {currentQueueIndex + 1} of {safeQueue.length}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-sm font-semibold text-white/80">
                    No song loaded
                  </div>
                  <div className="text-xs text-purple-100">
                    Select a song from your library
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Main Controls */}
          <div className="flex items-center gap-1">
            {/* Shuffle */}
            <button
              onClick={onShuffleToggle}
              className={`p-2 rounded-lg transition-colors ${
                shuffleEnabled ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white hover:bg-white/10'
              }`}
              title={shuffleEnabled ? 'Shuffle: On' : 'Shuffle: Off'}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
              </svg>
            </button>

            {/* Previous */}
            <button
              onClick={onPrevious}
              disabled={!hasPrevious}
              className="p-2 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/10"
              title="Previous song"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8.445 14.832A1 1 0 0010 14v-2.798l5.445 3.63A1 1 0 0017 14V6a1 1 0 00-1.555-.832L10 8.798V6a1 1 0 00-1.555-.832l-6 4a1 1 0 000 1.664l6 4z" />
              </svg>
            </button>

            {/* Back 5 seconds */}
            <button
              onClick={() => onSeek(Math.max(0, currentTime - 5))}
              className="p-2 rounded-lg transition-colors hover:bg-white/10"
              title="Rewind 5 seconds"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0019 16V8a1 1 0 00-1.6-.8l-5.333 4zM4.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0011 16V8a1 1 0 00-1.6-.8l-5.334 4z" />
              </svg>
            </button>

            {/* Play/Pause */}
            <button
              onClick={onPlayPause}
              className="p-3 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
              title={showAnimation ? 'Pause' : 'Play'}
            >
              {showAnimation ? (
                // Currently playing - show PAUSE icon
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              ) : (
                // Paused or stopped - show PLAY icon
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                </svg>
              )}
            </button>

            {/* Restart */}
            <button
              onClick={() => onSeek(0)}
              className="p-2 rounded-lg transition-colors hover:bg-white/10"
              title="Restart song"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>

            {/* Next */}
            <button
              onClick={onNext}
              disabled={!hasNext}
              className="p-2 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/10"
              title="Next song"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4.555 5.168A1 1 0 003 6v8a1 1 0 001.555.832L10 11.202V14a1 1 0 001.555.832l6-4a1 1 0 000-1.664l-6-4A1 1 0 0010 6v2.798l-5.445-3.63z" />
              </svg>
            </button>

            {/* Loop */}
            <button
              onClick={cycleLoopMode}
              className={`p-2 rounded-lg transition-colors relative ${
                loopMode !== 'none' ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white hover:bg-white/10'
              }`}
              title={loopMode === 'one' ? 'Loop: Current' : loopMode === 'all' ? 'Loop: All' : 'Loop: Off'}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {loopMode === 'one' && (
                <span className="absolute -top-1 -right-1 text-[9px] font-bold bg-white text-purple-600 rounded-full w-3.5 h-3.5 flex items-center justify-center">1</span>
              )}
            </button>
          </div>

          {/* Volume & Exit Controls */}
          <div className="flex items-center gap-3">
            {/* Volume Control */}
            <div className="hidden md:flex items-center gap-2">
              <button
                onClick={() => onVolumeChange(volume === 0 ? 0.8 : 0)}
                className="p-1.5 hover:bg-white/10 rounded transition-colors"
                title={volume === 0 ? 'Unmute' : 'Mute'}
              >
                {volume === 0 ? (
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                ) : volume < 0.5 ? (
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
              <div 
                ref={volumeBarRef}
                className="relative w-20 h-1 bg-white/20 rounded-full cursor-pointer hover:h-1.5 transition-all group"
                onMouseDown={handleVolumeMouseDown}
                onClick={(e) => e.stopPropagation()}
              >
                <div 
                  className="absolute h-full bg-white rounded-full transition-all"
                  style={{ width: `${volume * 100}%` }}
                />
                <div 
                  className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ left: `calc(${volume * 100}% - 5px)` }}
                />
              </div>
            </div>

            {/* Queue Button */}
            <button
              onClick={onToggleQueue}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              title="Show queue"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-all hover:scale-110"
              title="Close music player"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
